import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { getLogEvent, getLogEvents, getLogFiles } from 'apis/controllers/logViewer';

const PAGE_SIZE = 75;
const LEVELS = ['Verbose', 'Debug', 'Information', 'Warning', 'Error', 'Fatal'];

const formatBytes = (bytes = 0) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => (value ? new Date(value).toLocaleString('el-GR') : '—');

const levelColor = (level) => {
    switch ((level || '').toLowerCase()) {
        case 'fatal':
        case 'error':
            return 'error';
        case 'warning':
            return 'warning';
        case 'debug':
        case 'verbose':
            return 'default';
        default:
            return 'success';
    }
};

const errorText = (error) => {
    if (typeof error === 'string') return error;
    return error?.message || 'Παρουσιάστηκε σφάλμα κατά την ανάκτηση των logs.';
};

const LogViewer = () => {
    const [files, setFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(true);
    const [selectedFileId, setSelectedFileId] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [level, setLevel] = useState('all');
    const [exceptionsOnly, setExceptionsOnly] = useState(false);
    const [groupSimilar, setGroupSimilar] = useState(true);
    const [cursor, setCursor] = useState(null);
    const [cursorHistory, setCursorHistory] = useState([]);
    const [page, setPage] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showRaw, setShowRaw] = useState(false);

    const resetPagination = () => {
        setCursor(null);
        setCursorHistory([]);
    };

    useEffect(() => {
        let active = true;
        setFilesLoading(true);
        getLogFiles()
            .then((data) => {
                if (!active) return;
                const nextFiles = Array.isArray(data) ? data : [];
                setFiles(nextFiles);
                setSelectedFileId((current) =>
                    nextFiles.some((file) => file.id === current) ? current : nextFiles[0]?.id || ''
                );
                setError(null);
            })
            .catch((requestError) => active && setError(requestError))
            .finally(() => active && setFilesLoading(false));

        return () => {
            active = false;
        };
    }, [refreshKey]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch(searchInput.trim());
            resetPagination();
        }, 350);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (!selectedFileId) {
            setPage(null);
            return undefined;
        }

        let active = true;
        setPageLoading(true);
        getLogEvents({
            fileId: selectedFileId,
            cursor,
            pageSize: PAGE_SIZE,
            search,
            level: level === 'all' ? undefined : level,
            exceptionsOnly
        })
            .then((data) => {
                if (!active) return;
                setPage(data);
                setError(null);
            })
            .catch((requestError) => active && setError(requestError))
            .finally(() => active && setPageLoading(false));

        return () => {
            active = false;
        };
    }, [selectedFileId, cursor, search, level, exceptionsOnly, refreshKey]);

    useEffect(() => {
        if (!selectedEventId) {
            setDetail(null);
            return undefined;
        }

        let active = true;
        setDetailLoading(true);
        getLogEvent(selectedEventId)
            .then((data) => active && setDetail(data))
            .catch((requestError) => active && setError(requestError))
            .finally(() => active && setDetailLoading(false));

        return () => {
            active = false;
        };
    }, [selectedEventId]);

    const visibleEvents = useMemo(() => {
        const items = page?.items || [];
        if (!groupSimilar) return items.map((event) => ({ event, count: 1 }));

        const groups = new Map();
        items.forEach((event) => {
            const existing = groups.get(event.groupKey);
            if (existing) existing.count += 1;
            else groups.set(event.groupKey, { event, count: 1 });
        });
        return [...groups.values()];
    }, [page?.items, groupSimilar]);

    const selectedFile = files.find((file) => file.id === selectedFileId);

    const handleFileChange = (event) => {
        setSelectedFileId(event.target.value);
        resetPagination();
    };

    const handleLevelChange = (event) => {
        setLevel(event.target.value);
        resetPagination();
    };

    const handleExceptionsOnly = (event) => {
        setExceptionsOnly(event.target.checked);
        resetPagination();
    };

    const goNext = () => {
        if (!page?.nextCursor) return;
        setCursorHistory((history) => [...history, cursor]);
        setCursor(page.nextCursor);
    };

    const goPrevious = () => {
        setCursorHistory((history) => {
            if (!history.length) return history;
            setCursor(history[history.length - 1]);
            return history.slice(0, -1);
        });
    };

    const closeDetail = () => {
        setSelectedEventId(null);
        setShowRaw(false);
    };

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto', p: { xs: 1, md: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <BugReportOutlinedIcon color="primary" />
                <Box>
                    <Typography variant="h5" fontWeight={700}>Αρχεία καταγραφής εφαρμογής</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Τα συμβάντα φορτώνονται τμηματικά, χωρίς να μεταφέρεται ολόκληρο το αρχείο στον browser.
                    </Typography>
                </Box>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 300 }} disabled={filesLoading}>
                        <InputLabel>Αρχείο</InputLabel>
                        <Select value={selectedFileId} label="Αρχείο" onChange={handleFileChange}>
                            {files.map((file) => (
                                <MenuItem key={file.id} value={file.id}>
                                    {file.fileName} · {formatBytes(file.size)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        fullWidth
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder="Μήνυμα, exception, AMKA, request path, correlation id…"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                            )
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Επίπεδο</InputLabel>
                        <Select value={level} label="Επίπεδο" onChange={handleLevelChange}>
                            <MenuItem value="all">Όλα</MenuItem>
                            {LEVELS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        sx={{ whiteSpace: 'nowrap' }}
                        control={<Checkbox checked={exceptionsOnly} onChange={handleExceptionsOnly} />}
                        label="Μόνο exceptions"
                    />

                    <Tooltip title="Ανανέωση">
                        <IconButton onClick={() => setRefreshKey((value) => value + 1)}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{errorText(error)}</Alert>}

            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        {selectedFile?.fileName || '—'} · {page?.matchedOnPage || 0} αποτελέσματα
                        {page?.scannedLines ? ` · ${page.scannedLines} γραμμές ελέγχθηκαν` : ''}
                        {page?.invalidLines ? ` · ${page.invalidLines} μη έγκυρες` : ''}
                    </Typography>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={<Checkbox size="small" checked={groupSimilar} onChange={(event) => setGroupSimilar(event.target.checked)} />}
                        label={<Typography variant="caption">Ομαδοποίηση παρόμοιων</Typography>}
                    />
                </Stack>
                <Divider />

                <TableContainer sx={{ minHeight: 320, maxHeight: '62vh' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 175 }}>Ημερομηνία</TableCell>
                                <TableCell sx={{ width: 115 }}>Επίπεδο</TableCell>
                                <TableCell>Μήνυμα</TableCell>
                                <TableCell sx={{ width: 260 }}>Πηγή / Correlation</TableCell>
                                <TableCell align="right" sx={{ width: 90 }}>Μέγεθος</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageLoading && !page ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 8 }}><CircularProgress size={28} /></TableCell></TableRow>
                            ) : visibleEvents.length === 0 ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 8, color: 'text.secondary' }}>Δεν βρέθηκαν συμβάντα.</TableCell></TableRow>
                            ) : visibleEvents.map(({ event, count }) => (
                                <TableRow
                                    hover
                                    key={event.eventId}
                                    onClick={() => setSelectedEventId(event.eventId)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatDate(event.timestamp)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={event.level} color={levelColor(event.level)} variant="outlined" />
                                        {count > 1 && <Chip size="small" label={`×${count}`} sx={{ ml: 0.5 }} />}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                            {event.message || event.exceptionType || '—'}
                                        </Typography>
                                        {event.exceptionType && (
                                            <Typography variant="caption" color="error" sx={{ fontFamily: 'monospace' }}>
                                                {event.exceptionType}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block" noWrap title={event.sourceContext || ''}>
                                            {event.sourceContext || '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" noWrap title={event.correlationId || event.requestPath || ''}>
                                            {event.correlationId || event.requestPath || ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right"><Typography variant="caption">{formatBytes(event.rawSizeBytes)}</Typography></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5 }}>
                    <Button startIcon={<NavigateBeforeIcon />} disabled={!cursorHistory.length || pageLoading} onClick={goPrevious}>
                        Προηγούμενα
                    </Button>
                    {pageLoading && <CircularProgress size={20} />}
                    <Button endIcon={<NavigateNextIcon />} disabled={!page?.nextCursor || pageLoading} onClick={goNext}>
                        Επόμενα
                    </Button>
                </Stack>
            </Paper>

            <Drawer anchor="right" open={Boolean(selectedEventId)} onClose={closeDetail} PaperProps={{ sx: { width: { xs: '100%', md: 760 }, p: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight={700}>Λεπτομέρειες συμβάντος</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(detail?.summary?.timestamp)}</Typography>
                    </Box>
                    <IconButton onClick={closeDetail}><CloseIcon /></IconButton>
                </Stack>
                <Divider sx={{ my: 2 }} />

                {detailLoading ? (
                    <Box sx={{ display: 'grid', placeItems: 'center', flex: 1 }}><CircularProgress /></Box>
                ) : detail ? (
                    <Stack spacing={2.5} sx={{ pb: 3 }}>
                        <Stack direction="row" spacing={1}>
                            <Chip size="small" label={detail.summary.level} color={levelColor(detail.summary.level)} variant="outlined" />
                            <Chip size="small" label={formatBytes(detail.summary.rawSizeBytes)} variant="outlined" />
                        </Stack>

                        <Box>
                            <Typography variant="overline" color="text.secondary">Μήνυμα</Typography>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{detail.summary.message || '—'}</Typography>
                        </Box>

                        {detail.exception && (
                            <Box>
                                <Typography variant="overline" color="text.secondary">Exception</Typography>
                                <Box component="pre" sx={{ maxHeight: '40vh', overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', bgcolor: '#111827', color: '#f9fafb', p: 2, borderRadius: 1, fontSize: 11 }}>
                                    {detail.exception}
                                </Box>
                            </Box>
                        )}

                        <Box>
                            <Typography variant="overline" color="text.secondary">Ιδιότητες</Typography>
                            <Box component="pre" sx={{ overflow: 'auto', bgcolor: '#f4f6f8', p: 2, borderRadius: 1, fontSize: 11 }}>
                                {JSON.stringify(detail.properties, null, 2)}
                            </Box>
                        </Box>

                        <Box>
                            <Button variant="outlined" size="small" onClick={() => setShowRaw((value) => !value)}>
                                {showRaw ? 'Απόκρυψη πλήρους JSON' : 'Προβολή πλήρους JSON'}
                            </Button>
                            {showRaw && (
                                <Box component="pre" sx={{ mt: 1, maxHeight: '50vh', overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', bgcolor: '#111827', color: '#f9fafb', p: 2, borderRadius: 1, fontSize: 11 }}>
                                    {detail.rawJson}
                                </Box>
                            )}
                        </Box>
                    </Stack>
                ) : (
                    <Alert severity="warning">Το συμβάν δεν βρέθηκε.</Alert>
                )}
            </Drawer>
        </Box>
    );
};

export default LogViewer;
